import { useEffect, useState } from "react";
import { Mail, Check, Loader2 } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { supabase } from "@/lib/supabase";

/**
 * Detect whether the user's current email is a synthetic placeholder we
 * generated when they signed in via Telegram for the first time. Pattern:
 * `tg-<digits>@telegram.users.local`.
 */
function isSyntheticEmail(email: string | null | undefined): boolean {
  if (!email) return true;
  return /@telegram\.users\.local$/i.test(email);
}

/**
 * "Email" block for /me/account. Two modes:
 *
 *   1. User already has a real email
 *      → show it, plus a "Change email" link that expands the form.
 *   2. User only has a synthetic placeholder (signed in via Telegram)
 *      → show a friendly call to action with the form already visible.
 *
 * Submission uses native `supabase.auth.updateUser({ email })`. Supabase
 * sends a confirmation link to the new address; until the user clicks
 * that link the email change isn't applied. We surface a "Check your
 * inbox" panel after submit so they know what to do next.
 *
 * Why this matters: a Telegram-only user can't sign in by email until
 * we attach one, and email is also the channel we fall back to when the
 * bot isn't an option (organizer mass-mail, daily digest, etc.).
 */
export function MyAccountEmailBlock() {
  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingNewEmail, setPendingNewEmail] = useState<string | null>(null);

  // Pull the current user once on mount. Refresh on auth state change so
  // we pick up email_change confirmations the moment they land.
  useEffect(() => {
    let mounted = true;

    async function read() {
      const { data } = await supabase.auth.getUser();
      if (!mounted) return;
      setCurrentEmail(data.user?.email ?? null);
    }
    void read();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      void read();
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const synthetic = isSyntheticEmail(currentEmail);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cleanEmail = newEmail.trim().toLowerCase();
    if (!cleanEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // emailRedirectTo: without this Supabase defaults to Site URL
      // (app.finally.plus) for the confirmation link, which is wrong
      // for the meetup app. The URL must be in the allow-list — it is
      // (supabase/config.toml has https://meetup.finally.plus/**).
      const { error: updateErr } = await supabase.auth.updateUser(
        { email: cleanEmail },
        { emailRedirectTo: `${window.location.origin}/auth/callback` },
      );
      if (updateErr) {
        // Supabase returns "User already registered" if the address is in
        // use by another account. Surface a clearer hint there.
        const msg = updateErr.message || "Couldn't send the confirmation email";
        if (/already/i.test(msg)) {
          throw new Error(
            "This email is already used by another Finally account. Sign in with it instead, then attach Telegram from there.",
          );
        }
        throw new Error(msg);
      }
      setPendingNewEmail(cleanEmail);
      setEditing(false);
      setNewEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't update email");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card padding="md" className="mt-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#fef3c7] text-[#b45309]">
          <Mail className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
            Email
          </p>
          {synthetic ? (
            <>
              <p className="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
                Add your email address
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-[#6c757d]">
                Attach an email so you can also sign in with a 6-digit code,
                and so we have a fallback channel for important updates.
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 break-all text-[15px] font-semibold text-[#1a1a1a]">
                {currentEmail}
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-[#6c757d]">
                You can sign in with a 6-digit code sent to this address.
              </p>
            </>
          )}
        </div>
      </div>

      {pendingNewEmail ? (
        <div className="mt-4 rounded-[12px] border border-[#bfdbfe] bg-[#eff6ff] p-4">
          <p className="flex items-start gap-2 text-[13px] leading-relaxed text-[#1d4ed8]">
            <Check className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <strong>Check your inbox.</strong> We sent a confirmation link to{" "}
              <strong>{pendingNewEmail}</strong>. Click it to finish attaching
              the address.
            </span>
          </p>
        </div>
      ) : null}

      {!pendingNewEmail && (synthetic || editing) ? (
        <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3">
          <Field label={synthetic ? "Your email" : "New email"}>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@gmail.com"
              required
              autoComplete="email"
            />
          </Field>
          {error ? (
            <p className="text-[12px] font-semibold text-[#991b1b]">{error}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" loading={submitting} disabled={!newEmail.trim()}>
              {submitting ? (
                <>
                  <Loader2 className="mr-1.5 inline-block h-3.5 w-3.5 animate-spin" />
                  Sending…
                </>
              ) : synthetic ? (
                "Send confirmation"
              ) : (
                "Save new email"
              )}
            </Button>
            {!synthetic ? (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setNewEmail("");
                  setError(null);
                }}
                disabled={submitting}
                className="h-10 rounded-[10px] px-3 text-[13px] font-semibold text-[#6c757d] hover:bg-[#f1f5f9] disabled:opacity-60"
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {!pendingNewEmail && !synthetic && !editing ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-3 inline-flex h-9 items-center gap-2 rounded-[10px] border border-[#e2e8f0] bg-white px-3.5 text-[12.5px] font-semibold text-[#475569] hover:bg-[#f8fafc]"
        >
          Change email
        </button>
      ) : null}
    </Card>
  );
}
